import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../atoms/Button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import type { ConfirmModalProps } from "../types/ConfirmModal.types";

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Sí",
  cancelText = "No",
  variant = "danger",
  isLoading = false,
}) => {
  const handleConfirm = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onConfirm();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-white rounded-lg shadow-2xl max-w-md w-full p-6 z-10"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
              duration: 0.3,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            {!isLoading && (
              <button
                type="button"
                onClick={handleCloseClick}
                className="absolute top-4 right-4 text-titan hover:text-tiber-700 transition-colors"
                aria-label="Cerrar"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            )}

            {/* Content */}
            <div className="pr-8">
              {title && (
                <h3 className="text-2xl font-bold text-tiber-900 mb-4">
                  {title}
                </h3>
              )}
              <p className="text-base text-tiber-800 mb-6">{message}</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                size="md"
                onClick={handleCloseClick}
                disabled={isLoading}
                type="button"
              >
                {cancelText}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleConfirm}
                isLoading={isLoading}
                disabled={isLoading}
                type="button"
                className={
                  variant === "danger"
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : variant === "warning"
                      ? "bg-amber-600 hover:bg-amber-700 text-white"
                      : "bg-tiber-600 hover:bg-tiber-700 text-white"
                }
              >
                {confirmText}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

ConfirmModal.displayName = "ConfirmModal";
